import { createSelector, MemoizedSelector, select } from '@ngrx/store';
import { map, pipe, startWith } from 'rxjs';

import { safeSelect, StateSelector } from '@fcom/core/selectors/selector-utils';
import {
  Amount,
  CartStatus,
  NO_VARIANTS,
  SelectedServiceData,
  SelectionsPerTraveler,
  ServiceAvailability,
  ServiceCatalog,
  ServiceCatalogCategory,
  ServiceCatalogCategoryServicesMap,
  ServiceCatalogSelections,
  ServiceCatalogSelectionsForAllTravelers,
  ServiceCatalogService,
  ServiceCatalogServices,
  ServiceCatalogTranslations,
  ServicesPerTraveler,
  ServicesState,
  ServicesStatus,
  TravelerService,
} from '@fcom/dapi/interfaces';
import {
  entrySet,
  isEmpty,
  isEmptyObjectOrHasEmptyValues,
  isPresent,
  mapValues,
  PreciseNumber,
  valuesOf,
} from '@fcom/core/utils';
import {
  Category,
  FinnairServiceCatalogCampaign,
  FinnairServiceCatalogItemV3,
  SubCategory,
} from '@fcom/dapi/api/models';
import {
  PaxServiceCategories,
  PaxServiceCategory,
} from '@fcom/booking/modules/pax-details/interfaces/pax-ancillaries.interface';
import { cartStatusSelector } from '@fcom/booking/store/selectors';
import { finShare } from '@fcom/rx';
import { CustomServiceType, ServiceSelections } from '@fcom/dapi/interfaces/services.interface';

import { CommonBookingFeatureState, QuantityForFragmentAndPassenger } from '../';
import { CommonBookingState } from '../store.interface';
import {
  asServiceCatalogCategory,
  getOverlappingCategoriesForCategory,
  isBoundBasedCategory,
  isSeatBundle,
  VariantUnitsPerFragment,
} from '../../modules/ancillaries/utils';
import { selectCommonBookingState } from './feature.selector';
import { seatBundleCatalogByFlightSelector } from './bundle.selector';
import { formatSelectionsByFragmentAndTraveler, removeEmptySelections } from '../../utils/service.utils';

const _servicesState = createSelector(selectCommonBookingState, (state: CommonBookingState) => state?.services);

const _servicesStateServices = createSelector(_servicesState, (ss: ServicesState) => ss?.services);

// Merge the seatmap-derived seat bundles across flights (the same bundle appears once per flight).
const _seatmapSeatBundles = createSelector(
  seatBundleCatalogByFlightSelector,
  (
    bundleCatalogByFlight: ReturnType<typeof seatBundleCatalogByFlightSelector.projector>
  ): FinnairServiceCatalogItemV3[] =>
    Object.entries(bundleCatalogByFlight ?? {}).reduce((acc, [_flightId, items]) => {
      items.forEach((bundle) => {
        const matchingBundleIndex = acc.findIndex((accBundle: FinnairServiceCatalogItemV3) => {
          return accBundle.category === bundle.category;
        });

        if (matchingBundleIndex !== -1) {
          acc[matchingBundleIndex] = {
            ...acc[matchingBundleIndex],
            services: { ...bundle?.services, ...acc[matchingBundleIndex].services },
            lowestPrice: { ...bundle?.lowestPrice, ...acc[matchingBundleIndex].lowestPrice },
          };
        } else {
          acc.push(bundle);
        }
      });

      return acc;
    }, [] as FinnairServiceCatalogItemV3[])
);

// Seat bundles sourced from the seatmap (the default source of truth) and prepended to the catalog
// categories, dropping any catalog-provided duplicate of the same seat bundle so it isn't shown twice.
const _serviceCatalog = createSelector(
  _servicesStateServices,
  _seatmapSeatBundles,
  (services: ServiceCatalog, seatmapBundleItems: FinnairServiceCatalogItemV3[]) => {
    if (!isPresent(services) || !seatmapBundleItems.length) {
      return services;
    }

    const seatmapBundleCategories = new Set(seatmapBundleItems.map((bundle) => bundle.category));
    const catalogRest = (services.categories || []).filter(
      (category) => !seatmapBundleCategories.has(category.category)
    );

    return {
      ...services,
      categories: [...seatmapBundleItems.map((bundle) => asServiceCatalogCategory(bundle)), ...catalogRest],
    };
  }
);

// Prefer the cart catalog seat bundle where present, falling back to the seatmap-derived one, so the same
// bundle is never shown twice. Ordering: cart catalog bundles, then seatmap fallbacks, then other categories.
const _serviceCatalogFromCatalogSeatBundles = createSelector(
  _servicesStateServices,
  _seatmapSeatBundles,
  (services: ServiceCatalog, seatmapBundleItems: FinnairServiceCatalogItemV3[]) => {
    if (!isPresent(services) || !seatmapBundleItems.length) {
      return services;
    }

    const catalogCategories = services.categories || [];
    const catalogBundleCategories = new Set(
      catalogCategories.filter((category) => isSeatBundle(category.category)).map((category) => category.category)
    );
    const seatmapBundles: ServiceCatalogCategory[] = seatmapBundleItems
      // Drop seatmap bundles that the cart catalog already provides (catalog takes precedence).
      .filter((bundle) => !catalogBundleCategories.has(bundle.category))
      .map((bundle) => asServiceCatalogCategory(bundle));
    const catalogBundles = catalogCategories.filter((category) => isSeatBundle(category.category));
    const catalogRest = catalogCategories.filter((category) => !isSeatBundle(category.category));

    return {
      ...services,
      categories: [...catalogBundles, ...seatmapBundles, ...catalogRest],
    };
  }
);

export const serviceAvailabilitySelector = createSelector(
  _servicesState,
  (ss: ServicesState) => ss?.serviceAvailability
);

const _serviceCatalogCampaigns = createSelector(_serviceCatalog, (catalog: ServiceCatalog) => catalog?.campaigns);

const _campaignsShown = createSelector(_servicesState, (state: ServicesState) => state?.campaignsShown ?? []);
export const serviceSelections = createSelector(_servicesState, (ss: ServicesState) => ss?.selections);

const _servicesStatus = createSelector(_servicesState, (ss: ServicesState) => ss?.status);

const _servicesForCategory = (category: Category) =>
  createSelector(_serviceCatalog, (sc) =>
    sc ? sc.categories?.find((c) => c.category === category)?.services || {} : undefined
  );

const _serviceCategories = createSelector(_serviceCatalog, (sc) => sc?.categories);

const _serviceCategoriesFromCatalogSeatBundles = createSelector(
  _serviceCatalogFromCatalogSeatBundles,
  (sc) => sc?.categories
);

export const serviceCategoriesMapSelector = createSelector(
  _serviceCatalog,
  (sc): ServiceCatalogCategoryServicesMap | undefined =>
    sc?.categories.reduce<ServiceCatalogCategoryServicesMap>((categories, category) => {
      categories[category.category] = category.services;
      return categories;
    }, {})
);

/**
 * Maps each service category to its catalog translations (title, description, etc.). The sibling of
 * serviceCategoriesMapSelector (which maps categories to services), for consumers that need a category's
 * copy rather than its services.
 */
export const serviceCategoryTranslationsMapSelector: MemoizedSelector<
  CommonBookingFeatureState,
  Partial<Record<Category, ServiceCatalogTranslations>>
> = createSelector(_serviceCategories, (categories): Partial<Record<Category, ServiceCatalogTranslations>> =>
  (categories ?? []).reduce(
    (translationsByCategory, category) => {
      translationsByCategory[category.category] = category.translations;
      return translationsByCategory;
    },
    {} as Partial<Record<Category, ServiceCatalogTranslations>>
  )
);

/**
 * The catalog's service categories as plain category codes, in the catalog-defined display order. This is
 * the ordering authority for the shopping cart's purchasable product lines (see
 * `ShoppingCartBoundServicesComponent.productGroups`), mirroring how Travel Extras lists its categories. Seat
 * bundles are not (yet) part of the catalog, so consumers pin those separately.
 */
export const serviceCategoryOrderSelector: MemoizedSelector<CommonBookingFeatureState, Category[]> = createSelector(
  _serviceCategories,
  (categories): Category[] => (categories ?? []).map((category) => category.category)
);

export const servicesReadySelector = createSelector(
  cartStatusSelector,
  _servicesStatus,
  (cartStatus: CartStatus, servicesStatus: ServicesStatus) => {
    return cartStatus === CartStatus.OK && servicesStatus === ServicesStatus.OK;
  }
);

const _servicesForFragmentAndCategory = (fragmentId: string, category: Category) =>
  createSelector(_servicesForCategory(category), (ss: ServiceCatalogServices) =>
    ss ? ss[fragmentId] || {} : undefined
  );

export const serviceSelectionsForCategory = <T>(
  category: Category | SubCategory
): MemoizedSelector<CommonBookingFeatureState, ServiceCatalogSelections<T>> =>
  createSelector(_servicesState, (ss: ServicesState): ServiceCatalogSelections<T> => {
    if (!ss) {
      return undefined;
    }
    return ss.selections?.[category] ?? {};
  });

const _selectedServiceQuantitiesPerFragmentAndPax = (category: Category) =>
  createSelector(
    serviceSelectionsForCategory<TravelerService | TravelerService[]>(category),
    (selections): VariantUnitsPerFragment =>
      Object.entries(selections ?? {}).reduce<VariantUnitsPerFragment>(
        (unitsPerFragment, [fragmentId, selectionsPerTraveler]) => {
          Object.entries(selectionsPerTraveler ?? {}).forEach(([travelerId, selection]) => {
            []
              .concat(selection)
              .filter(isPresent)
              .filter(
                ({ includedInTicketType, includedInTierBenefit }) => !includedInTicketType && !includedInTierBenefit
              )
              .forEach(({ variant, quantity }) => {
                unitsPerFragment[fragmentId] = unitsPerFragment[fragmentId] ?? {};
                unitsPerFragment[fragmentId][travelerId] = unitsPerFragment[fragmentId][travelerId] ?? {};
                unitsPerFragment[fragmentId][travelerId][variant] =
                  (unitsPerFragment[fragmentId][travelerId][variant] ?? 0) + (quantity ?? 1);
              });
          });

          return unitsPerFragment;
        },
        {}
      )
  );

export const selectedServiceQuantitiesPerFragmentAndPaxSelector = (
  category: Category
): MemoizedSelector<CommonBookingFeatureState, VariantUnitsPerFragment> =>
  _selectedServiceQuantitiesPerFragmentAndPax(category);

const _variantsQuantity = (variants: string[]) =>
  createSelector(serviceSelections, (selections): QuantityForFragmentAndPassenger => {
    return Object.values(selections ?? {}).reduce(
      (allCategoriesQuantity, selection: ServiceCatalogSelections<TravelerService | TravelerService[]>) => {
        return entrySet(selection).reduce((quantitiesForFragment, service) => {
          return entrySet(service.value).reduce((innerQuantitiesForFragment, p) => {
            const services = [].concat(p.value);
            const newQuantity = services
              .filter(Boolean)
              .filter((s) => variants.includes(s.variant) && !s.includedInTicketType && !s.includedInTierBenefit)
              .reduce(
                (quantity, passengerService) => quantity + (passengerService.quantity ?? 1),
                innerQuantitiesForFragment[service.key]?.[p.key] ?? 0
              );

            if (!innerQuantitiesForFragment[service.key]) {
              innerQuantitiesForFragment[service.key] = {};
            }
            innerQuantitiesForFragment[service.key][p.key] = newQuantity;
            return innerQuantitiesForFragment;
          }, quantitiesForFragment);
        }, allCategoriesQuantity);
      },
      {}
    );
  });

const _upsell = createSelector(_servicesState, (ss: ServicesState): SelectedServiceData => ss?.upsell);

const _serviceSelectionsForFragmentAndCategory = <T>(fragmentId: string, category: Category) =>
  createSelector(serviceSelectionsForCategory(category), (ss: ServiceCatalogSelections<T>) =>
    ss ? ss[fragmentId] || {} : undefined
  );

const _allSelectionsForCategory = <T>(category: Category) =>
  createSelector(
    _servicesForCategory(category),
    serviceSelectionsForCategory<T>(category),
    (services, selections): [ServiceCatalogServices, ServiceCatalogSelections<T>] => {
      return [services, selections];
    }
  );

const _selectionsForCategories = (categories: Category[]) =>
  createSelector(serviceSelections, (selections): ServiceSelections =>
    categories.reduce((allCategories, category: Category) => {
      return {
        ...allCategories,
        [category]: selections?.[category] ?? {},
      };
    }, {})
  );

const _serviceMapForCategories = (categories: Category[]) =>
  createSelector(serviceCategoriesMapSelector, _serviceCategories, (categoriesMap, serviceCategories) => {
    const categoryMap = categories.reduce((allCategories, category: Category) => {
      const serviceCategory = serviceCategories?.find((sc) => sc.category === category);
      return isPresent(categoriesMap?.[category])
        ? [
            ...allCategories,
            {
              services: categoriesMap[category],
              translations: serviceCategory?.translations ?? {},
              media: serviceCategory?.media ?? {},
              category,
              isBoundBased: isBoundBasedCategory(category),
            },
          ]
        : allCategories;
    }, []);

    return isEmptyObjectOrHasEmptyValues(categoryMap) ? undefined : categoryMap;
  });

/**
 * Helps caching the overlaps for each category
 */
const overlapSelectorsByCategory = new Map<
  Category,
  MemoizedSelector<CommonBookingFeatureState, ServiceCatalogSelections<TravelerService[]>>
>();

export const overlappingSelectionsForCategorySelector = (
  category: Category
): MemoizedSelector<CommonBookingFeatureState, ServiceCatalogSelections<TravelerService[]>> => {
  if (!overlapSelectorsByCategory.has(category)) {
    overlapSelectorsByCategory.set(
      category,
      createSelector(_selectionsForCategories(getOverlappingCategoriesForCategory(category)), (selections) => {
        const nonEmptySelections = removeEmptySelections(selections);
        return formatSelectionsByFragmentAndTraveler(nonEmptySelections);
      })
    );
  }

  return overlapSelectorsByCategory.get(category)!;
};

export const serviceCategories = (
  preferCatalogSeatBundles = false
): StateSelector<CommonBookingFeatureState, ServiceCatalogCategory[]> =>
  safeSelect(preferCatalogSeatBundles ? _serviceCategoriesFromCatalogSeatBundles : _serviceCategories);

export const serviceCategoriesMap = (): StateSelector<CommonBookingFeatureState, ServiceCatalogCategoryServicesMap> => {
  return safeSelect(serviceCategoriesMapSelector);
};

// Seatmap-sourced seat bundles are the default; cart-catalog sourcing stays a no-op until opted into via
// /devtool → Bundles, hence preferCatalogSeatBundles defaults to false.
export const serviceCatalog = (
  preferCatalogSeatBundles = false
): StateSelector<CommonBookingFeatureState, ServiceCatalog> =>
  select(preferCatalogSeatBundles ? _serviceCatalogFromCatalogSeatBundles : _serviceCatalog);

export const serviceAvailability = (): StateSelector<CommonBookingFeatureState, ServiceAvailability> =>
  safeSelect(serviceAvailabilitySelector);

export const serviceCatalogCampaigns = (): StateSelector<CommonBookingFeatureState, FinnairServiceCatalogCampaign[]> =>
  safeSelect(_serviceCatalogCampaigns);

export const serviceCatalogCampaignIdsForFragment = (
  fragmentId: string
): StateSelector<CommonBookingFeatureState, Record<Category, string[]>> =>
  pipe(
    safeSelect(_serviceCatalogCampaigns),
    map((campaigns) =>
      campaigns.reduce(
        (campaignsAndCategories, campaign) => {
          campaign.categories
            .filter((category) => !!category.fragments[fragmentId])
            .forEach((campaignCategory) => {
              if (campaignsAndCategories[campaignCategory.id]) {
                campaignsAndCategories[campaignCategory.id] = [
                  ...campaignsAndCategories[campaignCategory.id],
                  campaign.id,
                ];
              } else {
                campaignsAndCategories[campaignCategory.id] = [campaign.id];
              }
            });
          return campaignsAndCategories;
        },
        {} as Record<Category, string[]>
      )
    ),
    startWith({} as Record<Category, string[]>)
  );
export const campaignsShown = (): StateSelector<CommonBookingFeatureState, string[]> => safeSelect(_campaignsShown);

export const serviceSelectionsForFragment = <T>(
  fragmentId: string,
  category: Category
): StateSelector<CommonBookingFeatureState, SelectionsPerTraveler<T>> =>
  safeSelect(_serviceSelectionsForFragmentAndCategory<T>(fragmentId, category));

export const variantsQuantity = (
  variants: string[]
): StateSelector<CommonBookingFeatureState, QuantityForFragmentAndPassenger> => {
  return select(_variantsQuantity(variants));
};

export const serviceSelectionsForFragmentAndTraveler = <T>(
  fragmentId: string,
  travelerId: string,
  category: Category
): MemoizedSelector<CommonBookingFeatureState, T> =>
  createSelector(
    _serviceSelectionsForFragmentAndCategory(fragmentId, category),
    (selection: SelectionsPerTraveler<T>): T => selection?.[travelerId]
  );

export const servicesState = (): StateSelector<CommonBookingFeatureState, ServicesState> => select(_servicesState);
export const servicesStatus = (): StateSelector<CommonBookingFeatureState, ServicesStatus> => select(_servicesStatus);
export const servicesReady = (): StateSelector<CommonBookingFeatureState, boolean> => select(servicesReadySelector);
export const upsell = (): StateSelector<CommonBookingFeatureState, SelectedServiceData> => select(_upsell);

export const servicesForCategory = (
  category: Category
): StateSelector<CommonBookingFeatureState, ServiceCatalogServices> => safeSelect(_servicesForCategory(category));

export const servicesForFragmentAndCategory = (
  fragmentId: string,
  category: Category
): StateSelector<CommonBookingFeatureState, ServicesPerTraveler> =>
  safeSelect(_servicesForFragmentAndCategory(fragmentId, category));

export const serviceMapForCategories = (
  categories: Category[]
): StateSelector<CommonBookingFeatureState, ServiceCatalogCategory[]> =>
  safeSelect(_serviceMapForCategories(categories));

export const serviceSelectionsForCategoryAllTravelers = (
  category: Category
): StateSelector<CommonBookingFeatureState, ServiceCatalogSelectionsForAllTravelers> =>
  pipe(
    select(_allSelectionsForCategory<TravelerService | TravelerService[]>(category)),
    map(([services, selections]) => {
      return entrySet(selections).reduce((all, selection) => {
        all[selection.key] = entrySet(selection.value).reduce<TravelerService[]>(
          (allServices: TravelerService[], traveler) => {
            const servicesForTraveler: ServiceCatalogService[] = services?.[selection.key]?.[traveler.key] || [];
            const travelerServices = Array.isArray(traveler.value) ? traveler.value : [traveler.value];
            const translatedServices = travelerServices
              .filter(Boolean)
              .map(addTranslationsToService(servicesForTraveler));
            translatedServices.forEach((service) => (service.travelerId = traveler.key));
            return allServices.concat(translatedServices);
          },
          []
        );
        return all;
      }, {});
    }),
    finShare()
  );

export const serviceMapForCategoriesForTravelers = (
  travelerIds: string[],
  categories: Category[]
): StateSelector<CommonBookingFeatureState, PaxServiceCategories> =>
  pipe(
    safeSelect(_serviceMapForCategories(categories)),
    map((serviceCategories: PaxServiceCategory[]) => {
      return travelerIds.reduce((serviceMapForTravelers, travelerId) => {
        return {
          ...serviceMapForTravelers,
          [travelerId]: serviceCategories.map((category) => ({
            ...category,
            services: mapValues(category.services, (fragment, fragmentId) => {
              const travelerOrGroup = fragmentId === CustomServiceType.JOURNEY ? CustomServiceType.GROUP : travelerId;
              // Filter out unwanted service variants and subCategories
              return fragment?.[travelerOrGroup]
                ?.filter((service) => ![...NO_VARIANTS, '_NOLOUNGE'].includes(service.variant))
                .filter((service) => ![SubCategory.HEAVY_OR_LARGE].includes(service.subCategory));
            }),
          })),
        };
      }, {});
    })
  );

export const selectionMapForCategoriesForTravelers = (
  travelerIds: string[],
  categories: Category[]
): StateSelector<CommonBookingFeatureState, ServiceSelections> =>
  pipe(
    safeSelect(_selectionsForCategories(categories)),
    map((selections) => {
      return travelerIds.reduce(
        (selectionsForTravelers, travelerId) => ({
          ...selectionsForTravelers,
          [travelerId]: mapValues(selections, (category) =>
            mapValues(category, (fragment, fragmentId) => {
              const travelerOrGroup = fragmentId === CustomServiceType.JOURNEY ? CustomServiceType.GROUP : travelerId;
              return fragment?.[travelerOrGroup];
            })
          ),
        }),
        {}
      );
    })
  );

/**
 * Returns the current non-empty selections of every category that overlaps the given one.
 * E.g. WIFI_MEAL => WIFI, MEAL, LOUNGE_WIFI, SEAT_MEAL, SEAT_WIFI (only those that actually have selections).
 */
export const overlappingSelectionsForCategory = (
  category: Category
): StateSelector<CommonBookingFeatureState, ServiceCatalogSelections<TravelerService[]>> =>
  pipe(select(overlappingSelectionsForCategorySelector(category)));

export const totalPriceForSelectionsForCategories = (
  categories: Category[]
): StateSelector<CommonBookingFeatureState, Amount> =>
  pipe(
    safeSelect(_selectionsForCategories(categories)),
    map((selections: ServiceSelections): Array<Amount & { quantity: number }> =>
      valuesOf(selections as Record<string, ServiceCatalogSelections<TravelerService | TravelerService[]>>)
        .flatMap((category) =>
          valuesOf(category).flatMap((fragment) =>
            valuesOf(fragment).flatMap((travelerSelection) => {
              if (Array.isArray(travelerSelection)) {
                return travelerSelection?.map((selection) => ({
                  ...selection.pricePerItem,
                  quantity: selection.quantity,
                }));
              }
              return isPresent(travelerSelection)
                ? { ...travelerSelection.pricePerItem, quantity: travelerSelection.quantity }
                : undefined;
            })
          )
        )
        .filter(Boolean)
    ),
    map((allPrices: Array<Amount & { quantity: number }>): Amount =>
      !isEmpty(allPrices)
        ? allPrices.reduce(
            (totalPrice, selectionPrice) => {
              const selectionSubTotal = PreciseNumber.fromString(selectionPrice.amount).multiply(
                PreciseNumber.from(selectionPrice.quantity, 0)
              );

              return {
                currencyCode: totalPrice.currencyCode || selectionPrice.currencyCode,
                amount: PreciseNumber.fromString(totalPrice.amount).add(selectionSubTotal).toString(),
              };
            },
            {
              currencyCode: null,
              amount: '0',
            }
          )
        : undefined
    )
  );

function addTranslationsToService(servicesForTraveler: ServiceCatalogService[]) {
  return (travelerService): TravelerService => {
    const serviceForTraveler = servicesForTraveler.find((s) => s.variant === travelerService.variant);
    if (serviceForTraveler) {
      return {
        ...travelerService,
        ...(travelerService.category === Category.SEAT ? { seatNumber: travelerService.variant } : {}),
        title: serviceForTraveler.translations?.title,
        maxBaggageWeight: serviceForTraveler.parameters?.baggageWeight,
      };
    }
    return travelerService;
  };
}
